// GiftPromotionHandler.jsx
import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import env from '../resource/env.js';
import { NULL_FUNCTION } from '../resource/defaults.js';
import { PRESET_PREVIEW } from '../resource/liveStreamConstants.js';

export const GiftPromotionHandler = ({
  // Remote config
  isGiftPromotionEnabled = false,
  giftPromotionDelaySeconds = env.FREE_LIVESTREAM_GIFT_POPUP_THRESHOLD_SECONDS,
  promotionWindowDurationSeconds = env.FREE_LIVESTREAM_GIFT_POPUP_LIMIT_WINDOW_SECONDS,
  maxPromotionDisplayCount = env.FREE_LIVESTREAM_GIFT_POPUP_MAX_SHOW_COUNT,
  backgroundIdleSeconds = env.FREE_LIVESTREAM_GIFT_POPUP_BACKGROUND_IDLE_SECONDS,
  // User state
  promotionDisplayCount = 0,
  promotionWindowStartTime = null,
  lastGiftTime = null,
  // Actions
  setPromotionDisplayCount = NULL_FUNCTION,
  setPromotionWindowStartTime = NULL_FUNCTION,
  setIsGiftPromotionModalVisible = NULL_FUNCTION,
  // Trigger conditions
  streamStatus = '',
  isAuthed = false,
  isLiveStreaming = false,
  shouldShowLivestreamCta = false,
  hasStreamToken = false,
  // Redux modal state
  isGiftPromotionModalVisible = false,
}) => {
  const [hasShownPromotionInSession, setHasShownPromotionInSession] =
    useState(false);

  // 1. initialTimer: used to check if the promotion can be shown after giftPromotionDelaySeconds
  // 2. resetTimer: used to check if the promotion can be shown after promotionWindowDurationSeconds
  const timersRef = useRef({
    initialTimer: null,
    resetTimer: null,
  });

  // Records when the PWA went to background, used to measure how long
  // it stayed in the background.
  const backgroundStartTimestampRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (timersRef.current.initialTimer) {
      clearTimeout(timersRef.current.initialTimer);
      timersRef.current.initialTimer = null;
    }
    if (timersRef.current.resetTimer) {
      clearTimeout(timersRef.current.resetTimer);
      timersRef.current.resetTimer = null;
    }
  }, []);

  const hasBasicConditions = useCallback(() => {
    // Check if user has spent diamonds within the threshold time
    if (lastGiftTime) {
      const now = Date.now();
      const thresholdMs = giftPromotionDelaySeconds * 1000;
      const timeSinceLastGift = now - lastGiftTime;
      // If user sent a gift within the threshold seconds, don't show promotion
      if (timeSinceLastGift < thresholdMs) {
        return false;
      }
    }

    return (
      isGiftPromotionEnabled &&
      streamStatus === PRESET_PREVIEW &&
      isAuthed &&
      isLiveStreaming &&
      !shouldShowLivestreamCta
    );
  }, [
    isGiftPromotionEnabled,
    streamStatus,
    isAuthed,
    isLiveStreaming,
    shouldShowLivestreamCta,
    lastGiftTime,
    giftPromotionDelaySeconds,
  ]);

  const canShowPromotion = useCallback(() => {
    if (!hasBasicConditions()) {
      return false;
    }

    // check if the promotion has been shown in this stream or is currently being shown
    if (hasShownPromotionInSession || isGiftPromotionModalVisible) {
      return false;
    }

    // when promotionWindowDurationSeconds = 0
    // skip time window and max count restrictions
    if (promotionWindowDurationSeconds === 0) {
      return true;
    }

    const now = Date.now();
    const limitWindowMs = promotionWindowDurationSeconds * 1000;

    // check if in time window
    if (promotionWindowStartTime) {
      const timeElapsed = now - promotionWindowStartTime;

      if (timeElapsed < limitWindowMs) {
        // check if show count is less than max show count
        return promotionDisplayCount < maxPromotionDisplayCount;
      }

      // over time window, can show
      return true;
    }

    // first time in livestream, can show
    return true;
  }, [
    hasBasicConditions,
    hasShownPromotionInSession,
    isGiftPromotionModalVisible,
    promotionWindowDurationSeconds,
    promotionWindowStartTime,
    promotionDisplayCount,
    maxPromotionDisplayCount,
  ]);

  // show promotion and handle the redux state update
  const showPromotion = useCallback(() => {
    if (promotionWindowDurationSeconds === 0) {
      setIsGiftPromotionModalVisible(true);
      setHasShownPromotionInSession(true);
      return;
    }

    const now = Date.now();
    const limitWindowMs = promotionWindowDurationSeconds * 1000;

    if (promotionWindowStartTime) {
      const timeElapsed = now - promotionWindowStartTime;
      if (timeElapsed >= limitWindowMs) {
        // over time window, reset count and time
        setPromotionWindowStartTime({ timestamp: now });
        setPromotionDisplayCount({ count: 1 });
      } else {
        // in time window, increase count
        setPromotionDisplayCount({ count: promotionDisplayCount + 1 });
      }
    } else {
      // first time in livestream, record time and count
      setPromotionWindowStartTime({ timestamp: now });
      setPromotionDisplayCount({ count: 1 });
    }

    setIsGiftPromotionModalVisible(true);
    setHasShownPromotionInSession(true);
  }, [
    promotionWindowDurationSeconds,
    promotionWindowStartTime,
    promotionDisplayCount,
    setPromotionDisplayCount,
    setPromotionWindowStartTime,
    setIsGiftPromotionModalVisible,
  ]);

  // This is for the case that the user has reached the max promotion show count
  // and the user is still in the livestream, so we need to schedule the next check
  const scheduleNextCheck = useCallback(() => {
    if (promotionWindowDurationSeconds === 0) {
      return;
    }

    if (!promotionWindowStartTime) {
      return;
    }

    const now = Date.now();
    const limitWindowMs = promotionWindowDurationSeconds * 1000;

    const timeElapsed = now - promotionWindowStartTime;
    const timeUntilReset = limitWindowMs - timeElapsed;

    if (timeUntilReset > 0) {
      if (timersRef.current.resetTimer) {
        clearTimeout(timersRef.current.resetTimer);
      }

      timersRef.current.resetTimer = setTimeout(() => {
        if (canShowPromotion()) {
          showPromotion();
        }
      }, timeUntilReset + 1000); // add 1 second to ensure over the time window
    }
  }, [
    promotionWindowStartTime,
    promotionWindowDurationSeconds,
    canShowPromotion,
    showPromotion,
  ]);

  // Main logic flow
  useEffect(() => {
    if (!hasBasicConditions()) {
      return;
    }

    if (hasShownPromotionInSession) {
      return;
    }

    clearAllTimers();

    timersRef.current.initialTimer = setTimeout(() => {
      if (canShowPromotion()) {
        showPromotion();
      } else if (
        promotionWindowDurationSeconds !== 0 &&
        promotionDisplayCount >= maxPromotionDisplayCount
      ) {
        // hit the max show count, schedule the next check
        // only when time window is not zero
        scheduleNextCheck();
      }
    }, giftPromotionDelaySeconds * 1000);

    return () => {
      clearAllTimers();
    };
  }, [
    hasBasicConditions,
    hasShownPromotionInSession,
    giftPromotionDelaySeconds,
    promotionWindowDurationSeconds,
    promotionWindowStartTime,
    setPromotionWindowStartTime,
    promotionDisplayCount,
    maxPromotionDisplayCount,
    canShowPromotion,
    showPromotion,
    scheduleNextCheck,
    clearAllTimers,
  ]);

  // when user leave the livestream
  useEffect(() => {
    if (!isLiveStreaming) {
      clearAllTimers();
      setHasShownPromotionInSession(false);
      setIsGiftPromotionModalVisible(false);
    }

    return () => {
      clearAllTimers();
      setIsGiftPromotionModalVisible(false);
    };
  }, [isLiveStreaming, clearAllTimers, setIsGiftPromotionModalVisible]);

  // when modal is open and stream status changes from preview(free mode) to sd (paid mode)
  // check if user has permission, if not, close the modal
  useEffect(() => {
    if (
      streamStatus !== PRESET_PREVIEW &&
      !hasStreamToken &&
      isGiftPromotionModalVisible
    ) {
      setIsGiftPromotionModalVisible(false);
      clearAllTimers();
    }
  }, [
    streamStatus,
    hasStreamToken,
    isGiftPromotionModalVisible,
    setIsGiftPromotionModalVisible,
    clearAllTimers,
  ]);

  // When the PWA returns from background, close the modal so the user
  // does not send a gift based on outdated livestream state.
  useEffect(() => {
    const backgroundIdleMsec = backgroundIdleSeconds * 1000;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        backgroundStartTimestampRef.current = Date.now();
        return;
      }
      const backgroundStartTimestamp = backgroundStartTimestampRef.current;
      backgroundStartTimestampRef.current = null;
      if (
        backgroundStartTimestamp &&
        isGiftPromotionModalVisible &&
        Date.now() - backgroundStartTimestamp >= backgroundIdleMsec
      ) {
        setIsGiftPromotionModalVisible(false, { skipAnimation: true });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    backgroundIdleSeconds,
    isGiftPromotionModalVisible,
    setIsGiftPromotionModalVisible,
  ]);

  // only run logic, no UI rendering
  // UI rendering is handled by the GiftPromotionModal component
  return null;
};

GiftPromotionHandler.propTypes = {
  isGiftPromotionEnabled: PropTypes.bool,
  giftPromotionDelaySeconds: PropTypes.number,
  promotionWindowDurationSeconds: PropTypes.number,
  maxPromotionDisplayCount: PropTypes.number,
  backgroundIdleSeconds: PropTypes.number,
  promotionDisplayCount: PropTypes.number,
  promotionWindowStartTime: PropTypes.number,
  lastGiftTime: PropTypes.number,
  setPromotionDisplayCount: PropTypes.func,
  setPromotionWindowStartTime: PropTypes.func,
  setIsGiftPromotionModalVisible: PropTypes.func,
  streamStatus: PropTypes.string,
  isAuthed: PropTypes.bool,
  isLiveStreaming: PropTypes.bool,
  shouldShowLivestreamCta: PropTypes.bool,
  hasStreamToken: PropTypes.bool,
  isGiftPromotionModalVisible: PropTypes.bool,
};

export default GiftPromotionHandler;
